// RebrandingSystem.js - Major corporate identity change
// $2M to reset all negative relations to 0, keep positives
// Can only do once per year

// ============================================
// REBRANDING MECHANICS
// ============================================

export const REBRAND_CONFIG = {
  cost: 2000000 * 100,           // $2M
  cooldownDays: 365,             // Once per year
  
  // What rebranding does:
  effects: {
    resetNegativeRelations: true,  // All negative airport relations -> 0
    keepPositiveRelations: true,   // Positive relations unchanged
    resetCompetitorHostility: 0.5, // Competitor hostility reduced by 50%
    reputationReset: false,        // Reputation score is NOT reset
  },
  
  // What player loses:
  costs: {
    liveryRedesign: true,          // Must create new livery
    marketingReset: true,          // Active campaigns cancelled
    loyaltyProgramReset: true,     // Loyalty program progress reset
  },
};

// ============================================
// REBRANDING STATE
// ============================================

export function createRebrandingState() {
  return {
    lastRebrandAt: null,
    timesRebranded: 0,
    previousBrands: [],
  };
}

// ============================================
// REBRANDING ACTIONS
// ============================================

export function canRebrand(rebrandState, cash, gameDay) {
  // Check cooldown
  if (rebrandState.lastRebrandAt) {
    const daysSince = gameDay - rebrandState.lastRebrandAt;
    if (daysSince < REBRAND_CONFIG.cooldownDays) {
      return {
        canRebrand: false,
        reason: `Must wait ${REBRAND_CONFIG.cooldownDays - daysSince} more days`,
        daysRemaining: REBRAND_CONFIG.cooldownDays - daysSince,
      };
    }
  }
  
  // Check cash
  if (cash < REBRAND_CONFIG.cost) {
    return {
      canRebrand: false,
      reason: 'Insufficient funds ($2M required)',
      shortfall: REBRAND_CONFIG.cost - cash,
    };
  }
  
  return {
    canRebrand: true,
    cost: REBRAND_CONFIG.cost,
  };
}

export function executeRebrand(rebrandState, relationsState, airlineData, gameDay) {
  // Store old brand info
  const oldBrand = {
    name: airlineData.name,
    callsign: airlineData.callsign,
    livery: airlineData.livery_data,
    rebrandedAt: gameDay,
  };
  
  // Reset negative relations
  const newRelationsState = resetNegativeRelations(relationsState);
  
  // Update rebrand state
  const newRebrandState = {
    lastRebrandAt: gameDay,
    timesRebranded: rebrandState.timesRebranded + 1,
    previousBrands: [...rebrandState.previousBrands, oldBrand],
  };
  
  return {
    success: true,
    rebrandState: newRebrandState,
    relationsState: newRelationsState,
    cost: REBRAND_CONFIG.cost,
    effects: {
      relationsReset: countResetRelations(relationsState, newRelationsState),
      requiresNewLivery: true,
      campaignsCancelled: true,
    },
    message: 'Rebrand complete. Your slate is clean. Design your new identity.',
  };
}

function resetNegativeRelations(relationsState) {
  if (!relationsState) return relationsState;
  
  const newState = JSON.parse(JSON.stringify(relationsState)); // Deep clone
  
  // Reset airport relations
  Object.keys(newState.airports || {}).forEach(code => {
    const airport = newState.airports[code];
    if (airport.loyalty?.PLAYER && airport.loyalty.PLAYER < 0) {
      airport.loyalty.PLAYER = 0;
    }
  });
  
  // Reduce competitor hostility by 50%
  Object.keys(newState.competitors || {}).forEach(id => {
    const competitor = newState.competitors[id];
    if (competitor.playerRelation && competitor.playerRelation < 0) {
      competitor.playerRelation = Math.round(competitor.playerRelation * 0.5);
    }
  });
  
  return newState;
}

function countResetRelations(oldState, newState) {
  let airportsReset = 0;
  let competitorsReset = 0;
  
  Object.keys(oldState?.airports || {}).forEach(code => {
    const oldRelation = oldState.airports[code]?.loyalty?.PLAYER || 0;
    const newRelation = newState.airports[code]?.loyalty?.PLAYER || 0;
    if (oldRelation < 0 && newRelation === 0) {
      airportsReset++;
    }
  });
  
  Object.keys(oldState?.competitors || {}).forEach(id => {
    const oldRelation = oldState.competitors[id]?.playerRelation || 0;
    const newRelation = newState.competitors[id]?.playerRelation || 0;
    if (newRelation > oldRelation) {
      competitorsReset++;
    }
  });
  
  return { airportsReset, competitorsReset };
}

// ============================================
// NEW BRAND GENERATION
// ============================================

export function generateBrandSuggestions() {
  // Generate some random airline name suggestions
  const prefixes = [
    'Pacific', 'Mountain', 'Coastal', 'Northern', 'Southern', 'Western', 'Eastern',
    'Blue', 'Silver', 'Golden', 'Crystal', 'Summit', 'Valley', 'Sky', 'Cloud',
    'Swift', 'Arrow', 'Falcon', 'Eagle', 'Horizon', 'Frontier', 'Pioneer',
  ];
  
  const suffixes = [
    'Air', 'Airlines', 'Airways', 'Aviation', 'Express', 'Wings', 'Jet', 'Flight',
  ];
  
  const suggestions = [];
  for (let i = 0; i < 6; i++) {
    const prefix = prefixes[Math.floor(Math.random() * prefixes.length)];
    const suffix = suffixes[Math.floor(Math.random() * suffixes.length)];
    const name = `${prefix} ${suffix}`;
    const callsign = prefix.substring(0, 3).toUpperCase();
    
    suggestions.push({ name, callsign });
  }
  
  return suggestions;
}

// ============================================
// EXPORTS
// ============================================

export default {
  REBRAND_CONFIG,
  createRebrandingState,
  canRebrand,
  executeRebrand,
  generateBrandSuggestions,
};
